import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { i18n } = useTranslation();

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      const client = sessionStorage.getItem('client');
      const uid = sessionStorage.getItem('uid');
      const expiry = sessionStorage.getItem('expiry');
      const tokenType = sessionStorage.getItem('tokenType');


      if (accessToken && isValidToken(accessToken, expiry ||'0')) {
        setSession(accessToken, client, expiry, uid, tokenType);

        const res = await axios.get(endpoints.auth.me);
        if (res.status !== 200) {
          setSession(null, null, null, null, null);
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null,
            },
          });
        }
        console.log(res.data)
        const { user } = res.data

        dispatch({
          type: Types.INITIAL,
          payload: {
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      setSession(null, null, null, null, null);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (email: string, password: string) => {
    try{
      const data = {
        email,
        password,
      };

      const res = await axios.post(endpoints.auth.login, data);

      const user = res.data.user;
      const accessToken = res.headers['access-token'];
      const client = res.headers.client;
      const uid = res.headers.uid;
      const expiry = res.headers.expiry;
      const tokenType = res.headers['token-type'];

      setSession(accessToken, client, expiry, uid, tokenType) ;
      i18n.changeLanguage(user.language_setting)
      dispatch({
        type: Types.LOGIN,
        payload: {
          user,
        },
      });
      return user;
    }catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [i18n]);

  const register = useCallback(
    async (email: string, given_name: string, family_name: string, lang: string|null) => {
      const data = {
        email,
        given_name,
        family_name,
        lang
      };

      const res = await axios.post(endpoints.auth.register, data);

      const user = res.data.user;
      const accessToken = res.headers['access-token'];
      const client = res.headers.client;
      const uid = res.headers.uid;
      const expiry = res.headers.expiry;
      const tokenType = res.headers['token-type'];

      setSession(accessToken, client, expiry, uid, tokenType) ;

      sessionStorage.setItem(STORAGE_KEY, accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  const logout = useCallback(async () => {
    setSession(null, null, null, null, null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const refreshUserInfo = useCallback(async (): Promise<void> => {
    try {
      const res = await axios.get(endpoints.auth.me);
      if (res.status === 200) {
        const { user } = res.data;
        dispatch({
          type: Types.INITIAL,
          payload: {
            user,
          },
        });
      }
    } catch (error) {
      console.error('Failed to refresh user info:', error);
    }
  }, []);


  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      refreshUserInfo,
    }),
    [login, logout, register, state.user, status, refreshUserInfo]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
